import React, { useState } from 'react'
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText, CircularProgress, Tooltip, Divider, Switch, FormControlLabel } from '@mui/material'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import DescriptionIcon from '@mui/icons-material/Description'
import CodeIcon from '@mui/icons-material/Code'
import DataObjectIcon from '@mui/icons-material/DataObject'
import { exportToCSV, exportToJSON, exportToXML } from '../utils/recordExport'

/**
 * RecordExportButton - A reusable plugin component for exporting record data
 * as CSV, XML, or JSON. Drop into any ERP screen by passing the record data
 * and a sections config.
 *
 * Props:
 *  - recordData (object, required): The full state object from the parent screen
 *  - sections (array, required): Section config array from exportConfigs.js
 *  - fileName (string, required): Base file name (e.g., "Employee_John_Doe")
 *  - onFetchAll (async function, optional): Force-fetches all tab data via API.
 *      Must return the complete data object directly (not rely on state updates).
 *  - loadedSections (Set, optional): Which tab indices already have data
 *  - createSnack (function, optional): Notification callback
 */
export default function RecordExportButton({ recordData, sections, fileName, onFetchAll, loadedSections, createSnack }) {
  const [anchorEl, setAnchorEl] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [includeAll, setIncludeAll] = useState(true)
  const open = Boolean(anchorEl)

  const hasMultipleSections = onFetchAll && sections.some(
    (s) => s.tabValue !== undefined && s.tabValue !== 0
  )

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleExport = async (exportFn, formatLabel) => {
    handleClose()
    try {
      let dataToExport = recordData
      if (includeAll && onFetchAll) {
        setIsLoading(true)
        // onFetchAll force-fetches all tab APIs and returns the data directly,
        // bypassing React state so we don't need to wait for a re-render.
        const fetchedData = await onFetchAll()
        setIsLoading(false)
        if (fetchedData) {
          dataToExport = fetchedData
        }
      }
      exportFn(dataToExport, sections, fileName)
      if (createSnack) {
        createSnack(`Exported as ${formatLabel}`, 'success', 3000)
      }
    } catch (e) {
      console.error('Export failed:', e)
      setIsLoading(false)
      if (createSnack) {
        createSnack('Export failed. Please try again.', 'error', 3000)
      }
    }
  }

  return (
    <>
      <Tooltip title="Export Record">
        <IconButton
          onClick={handleClick}
          disabled={isLoading}
          size="small"
          style={{ marginLeft: '0.5rem' }}
        >
          {isLoading ? <CircularProgress size={20} /> : <FileDownloadIcon />}
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {hasMultipleSections && (
          <div>
            <MenuItem dense style={{ pointerEvents: 'none', opacity: 0.7 }}>
              <FormControlLabel
                style={{ pointerEvents: 'auto' }}
                control={
                  <Switch
                    size="small"
                    checked={includeAll}
                    onChange={(e) => setIncludeAll(e.target.checked)}
                  />
                }
                label={
                  <ListItemText
                    primary="Include all tabs"
                    // secondary="Fetches unloaded tab data"
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                }
              />
            </MenuItem>
            <Divider />
          </div>
        )}
        <MenuItem onClick={() => handleExport(exportToCSV, 'CSV')}>
          <ListItemIcon><DescriptionIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Export as CSV</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleExport(exportToJSON, 'JSON')}>
          <ListItemIcon><DataObjectIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Export as JSON</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleExport(exportToXML, 'XML')}>
          <ListItemIcon><CodeIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Export as XML</ListItemText>
        </MenuItem>
      </Menu>
    </>
  )
}
